import { useEffect, useState } from "react";
import {
  Layout,
  Card,
  BlockStack,
  Text,
  Button,
  Badge,
  InlineStack,
  Divider,
  ProgressBar,
  Banner,
  Box,
  SkeletonDisplayText,
  SkeletonBodyText,
  SkeletonPage,
  List,
} from "@shopify/polaris";

// Import unified data structure
import featureData from "../data/features.json";

import { FeedbackForm } from "../components/FeedbackForm";
import { QuotaDisplay } from "../components/QuotaDisplay";
import { SubscriptionCard } from "../components/SubscriptionCard";
import { ContractStatusCard } from "../components/ContractStatusCard";
import { useAppBridge } from "../hooks/useAppBridge";
import { useApi } from "../hooks/useApi";
import { createSubscriptionApi } from "../api/subscription";
import { useTranslation } from "../hooks/useTranslation";
import { useNotification } from "../hooks/useNotification";

interface HomePageProps {
  shop: string | null;
  tabFn: (tab: number) => void;
}

const HomePage = ({ shop, tabFn }: HomePageProps) => {
  const { t, locale } = useTranslation();
  const { app } = useAppBridge();
  const { Call } = useApi({ baseUrl: "/api" });
  const { showNotification } = useNotification();

  const [showWelcome, setShowWelcome] = useState(false);
  const [subscriptionInfo, setSubscriptionInfo] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  const subscriptionApi = createSubscriptionApi(Call);

  useEffect(() => {
    // Check sessionStorage for installed flag (set by App.tsx from URL param)
    const justInstalled = sessionStorage.getItem("just_installed");
    if (justInstalled === "true") {
      setShowWelcome(true);
      // Clear the flag so it doesn't show again
      sessionStorage.removeItem("just_installed");
      console.log("[HomePage] Showing welcome screen from sessionStorage");
    }

    // Check for subscription status from sessionStorage (set by App.tsx from URL param)
    const subscriptionStatus = sessionStorage.getItem("subscription_status");
    if (subscriptionStatus === "activated") {
      showNotification(
        t("dashboard.subscription.notifications.activated"),
        "success",
        "Success",
      );
      sessionStorage.removeItem("subscription_status");
    } else if (subscriptionStatus === "failed") {
      showNotification(
        t("dashboard.subscription.notifications.activationFailed"),
        "critical",
        "Error",
      );
      sessionStorage.removeItem("subscription_status");
    } else if (subscriptionStatus === "declined") {
      showNotification(
        t("dashboard.subscription.notifications.declined"),
        "warning",
        "Notice",
      );
      sessionStorage.removeItem("subscription_status");
    }
  }, [t, showNotification]);

  useEffect(() => {
    if (!app) return;

    fetchSubscriptionInfo();
  }, [app]);

  const fetchSubscriptionInfo = async () => {
    try {
      const response = await Call("/subscription/info", "GET");
      if (response.success) {
        setSubscriptionInfo(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch subscription info:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (plan?: string) => {
    setActionLoading(true);
    try {
      const response = await subscriptionApi.createSubscription(plan || "comply-starter");
      if (response.success && response.data.confirmation_url) {
        // Show redirecting state
        setRedirecting(true);
        // Redirect to Shopify's confirmation URL
        window.top!.location.href = response.data.confirmation_url;
      } else {
        showNotification(
          t("dashboard.subscription.notifications.couldNotInitiate"),
          "critical",
          "Error",
        );
        setActionLoading(false);
      }
    } catch (err) {
      showNotification(
        err instanceof Error
          ? err.message
          : t("dashboard.subscription.notifications.unknownError"),
        "critical",
        "Error",
      );
      setActionLoading(false);
    }
    // Don't set actionLoading to false in finally - keep it loading during redirect
  };

  const handleCancel = async () => {
    setActionLoading(true);
    try {
      const response = await subscriptionApi.cancelSubscription();
      if (response.success) {
        showNotification(
          t("dashboard.subscription.notifications.cancelled"),
          "success",
          "Success",
        );
        // Refresh subscription info
        await fetchSubscriptionInfo();
      } else {
        showNotification(
          t("dashboard.subscription.notifications.couldNotCancel"),
          "critical",
          "Error",
        );
      }
    } catch (err) {
      showNotification(
        err instanceof Error
          ? err.message
          : t("dashboard.subscription.notifications.unknownError"),
        "critical",
        "Error",
      );
    } finally {
      setActionLoading(false);
    }
  };

  // --- DATA PROCESSING (Oldest @ Bottom, Newest @ Top) ---
  const released = featureData
    .filter((f: any) => f.version && f.date)
    .sort(
      (a: any, b: any) =>
        new Date(b.date).getTime() - new Date(a.date).getTime(),
    );

  const future = featureData
    .filter((f: any) => !f.version)
    .sort((a: any, b: any) => (a.eta || "").localeCompare(b.eta || ""));

  const currentVersion = released[0] || {
    version: __APP_VERSION__,
    date: new Date().toISOString().split("T")[0],
  };

  // Show welcome screen if just installed (before loading check)
  if (showWelcome) {
    return (
      <Layout>
        <Layout.Section>
          <Card>
            <BlockStack gap="500">
              {/* Welcome Header */}
              <BlockStack gap="200">
                <Text variant="headingLg" as="h2">
                  {t("welcome.title")}
                </Text>
                <Text variant="bodyLg" as="p" tone="subdued">
                  {t("welcome.subtitle")}
                </Text>
              </BlockStack>

              <Divider />

              {/* What's Included */}
              <BlockStack gap="300">
                <Text variant="headingMd" as="h3">
                  {t("welcome.whatYouGet.title")}
                </Text>
                <Box paddingInlineStart="400">
                  <List type="bullet">
                    <List.Item>{t("welcome.whatYouGet.trial")}</List.Item>
                    <List.Item>{t("welcome.whatYouGet.automation")}</List.Item>
                    <List.Item>
                      {t("welcome.whatYouGet.pdfGeneration")}
                    </List.Item>
                    <List.Item>
                      {t("welcome.whatYouGet.realTimeTracking")}
                    </List.Item>
                    <List.Item>{t("welcome.whatYouGet.support")}</List.Item>
                  </List>
                </Box>
              </BlockStack>

              <Divider />

              {/* Next Steps */}
              <BlockStack gap="300">
                <Text variant="headingMd" as="h3">
                  {t("welcome.nextSteps.title")}
                </Text>
                <Box paddingInlineStart="400">
                  <List type="number">
                    <List.Item>{t("welcome.nextSteps.step1")}</List.Item>
                    <List.Item>{t("welcome.nextSteps.step2")}</List.Item>
                    <List.Item>{t("welcome.nextSteps.step3")}</List.Item>
                  </List>
                </Box>
              </BlockStack>

              <Divider />

              {/* Action Buttons */}
              <InlineStack gap="300">
                <Button
                  variant="primary"
                  onClick={() => {
                    setShowWelcome(false);
                    tabFn(2); // Go to Settings tab
                  }}
                >
                  {t("welcome.action.configure")}
                </Button>
                <Button onClick={() => setShowWelcome(false)}>
                  {t("welcome.action.goToDashboard")}
                </Button>
              </InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        {/* MAIN COLUMN */}
        <Layout.Section>
          <BlockStack gap="500">
            {/* Quota Display Skeleton */}
            <Card>
              <BlockStack gap="400">
                <SkeletonDisplayText size="small" />
                <BlockStack gap="200">
                  <SkeletonDisplayText size="medium" />
                  <SkeletonBodyText lines={1} />
                </BlockStack>
                <SkeletonBodyText lines={2} />
              </BlockStack>
            </Card>

            {/* Subscription Card Skeleton */}
            <Card>
              <BlockStack gap="400">
                <SkeletonDisplayText size="small" />
                <BlockStack gap="300">
                  <SkeletonDisplayText size="medium" />
                  <SkeletonBodyText lines={3} />
                </BlockStack>
              </BlockStack>
            </Card>

            {/* Product Updates Skeleton */}
            <Card>
              <BlockStack gap="400">
                <SkeletonDisplayText size="small" />
                <SkeletonBodyText lines={5} />
              </BlockStack>
            </Card>
          </BlockStack>
        </Layout.Section>

        {/* SIDEBAR */}
        <Layout.Section variant="oneThird">
          <BlockStack gap="400">
            {/* Current Version Skeleton */}
            <Card>
              <BlockStack gap="200">
                <SkeletonDisplayText size="small" />
                <SkeletonBodyText lines={1} />
              </BlockStack>
            </Card>

            {/* Feedback Form Skeleton */}
            <Card>
              <BlockStack gap="400">
                <SkeletonDisplayText size="small" />
                <SkeletonBodyText lines={8} />
              </BlockStack>
            </Card>
          </BlockStack>
        </Layout.Section>
      </Layout>
    );
  }

  const quota = subscriptionInfo?.quota || {};
  const isLocked = !quota.can_submit;
  const planStatus = quota.is_trial ? "TRIAL" : "PAID";

  return (
    // <Page fullWidth>
    <Layout>
      {/* REDIRECTING BANNER */}
      {redirecting && (
        <Layout.Section>
          <Banner title="Redirecting" tone="info">
            {t("dashboard.subscription.redirecting")}
          </Banner>
        </Layout.Section>
      )}

      {/* MAIN COLUMN */}
      <Layout.Section>
        <BlockStack gap="500">
          {/* PROVIDER CONTRACT STATUS */}
          <ContractStatusCard />

          {/* VIP BONUS SHOW-OFF — only while the bonus is still meaningful (trial, unused) */}
          {quota.is_trial && (subscriptionInfo?.promo_submissions_granted || 0) > 0 && (
            <Banner tone="success" title={t("dashboard.quota.vipBanner.title")}>
              <p>
                {t("dashboard.quota.vipBanner.body", {
                  bonus: subscriptionInfo?.promo_submissions_granted || 0,
                  total: quota.quota_limit || 0,
                })}
              </p>
            </Banner>
          )}

          {/* APP USAGE */}
          <QuotaDisplay
            totalUsed={quota.total_used || 0}
            quotaLimit={quota.quota_limit || (quota.is_trial ? 7 : 0)}
            submissionsRemaining={quota.submissions_remaining || 0}
            isTrial={quota.is_trial || false}
            billingCycleEnd={
              subscriptionInfo?.subscription_status === "cancelled"
                ? null
                : subscriptionInfo?.billing_cycle_end || null
            }
          />

          {/* SUBSCRIPTION PLAN CARD — hidden while trial has remaining submissions */}
          {!(quota.is_trial && (quota.submissions_remaining || 0) > 0) && (
            <SubscriptionCard
              subscriptionPlan={subscriptionInfo?.subscription_plan || null}
              subscriptionStatus={
                subscriptionInfo?.subscription_status || "trial"
              }
              isTrial={quota.is_trial || false}
              billingCycleStart={subscriptionInfo?.billing_cycle_start || null}
              billingCycleEnd={subscriptionInfo?.billing_cycle_end || null}
              submissionsRemaining={quota.submissions_remaining || 0}
              onUpgrade={handleUpgrade}
              onCancel={handleCancel}
              isLoading={actionLoading}
            />
          )}

          {/* PRODUCT UPDATES */}
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">
                {t("dashboard.productUpdates.title")}
              </Text>

              <BlockStack gap="300">
                {/* Future items first (coming soon) */}
                {future.slice(0, 3).map((item: any, i: number) => (
                  <InlineStack
                    key={`future-${i}`}
                    gap="200"
                    blockAlign="start"
                    wrap={false}
                  >
                    <Box paddingBlockStart="050">
                      <div
                        style={{
                          width: "6px",
                          height: "6px",
                          borderRadius: "50%",
                          backgroundColor: "var(--p-color-bg-fill-info)",
                        }}
                      />
                    </Box>
                    <BlockStack gap="050">
                      <InlineStack gap="200" blockAlign="center">
                        <Text variant="bodyMd" fontWeight="medium">
                          {item.title[locale] || item.title.el}
                        </Text>
                        {item.eta && (
                          <Badge size="small" tone="attention">
                            {item.eta}
                          </Badge>
                        )}
                      </InlineStack>
                      {item.description && (
                        <Text variant="bodyMd" tone="subdued">
                          {item.description[locale] || item.description.el}
                        </Text>
                      )}
                    </BlockStack>
                  </InlineStack>
                ))}

                {/* Released items (newest first) */}
                {released.slice(0, 5).map((item: any, i: number) => (
                  <BlockStack key={`released-${i}`} gap="050">
                    <InlineStack gap="200" blockAlign="center">
                      <div
                        style={{
                          width: "6px",
                          height: "6px",
                          borderRadius: "50%",
                          backgroundColor: "var(--p-color-bg-fill-success)",
                          flexShrink: 0,
                        }}
                      />
                      <Text as="p" variant="bodyMd" fontWeight="medium">
                        {item.title[locale] || item.title.el}
                      </Text>
                      <Badge size="small" tone="success">
                        v{item.version}
                      </Badge>
                    </InlineStack>
                    {item.description && (
                      <Text as="p" variant="bodyMd" tone="subdued">
                        {item.description[locale] || item.description.el}
                      </Text>
                    )}
                  </BlockStack>
                ))}
              </BlockStack>
            </BlockStack>
          </Card>
        </BlockStack>
      </Layout.Section>

      {/* SIDEBAR */}
      <Layout.Section variant="oneThird">
        <BlockStack gap="400">
          {/* CONTACT / HELP */}
          <Card>
            <BlockStack gap="300">
              <Text variant="headingMd" as="h3">
                {t("dashboard.contact.title")}
              </Text>
              <Text variant="bodyMd" as="p" tone="subdued">
                {t("dashboard.contact.subtitle")}
              </Text>
              <BlockStack gap="200">
                <InlineStack gap="200" blockAlign="center">
                  <Text variant="bodyMd" as="p" fontWeight="semibold">📞</Text>
                  <Text variant="bodyMd" as="p" fontWeight="semibold">698 359 6733</Text>
                </InlineStack>
                <InlineStack gap="200" blockAlign="center">
                  <Text variant="bodyMd" as="p" fontWeight="semibold">✉️</Text>
                  <Text variant="bodyMd" as="p">lefteris@weam.gr</Text>
                </InlineStack>
              </BlockStack>
            </BlockStack>
          </Card>

          {/* VERSION AT TOP */}
          <Card>
            <BlockStack gap="200">
              <InlineStack align="space-between">
                <Text variant="headingMd" as="h3">
                  {t("dashboard.currentVersion.title")}
                </Text>
                <Badge tone="info">v{currentVersion.version}</Badge>
              </InlineStack>
              <Text variant="bodySm" tone="subdued">
                {t("dashboard.currentVersion.updatedOn", {
                  date: currentVersion.date,
                })}
              </Text>
            </BlockStack>
          </Card>

          <Card>
            <FeedbackForm shop={shop} />
          </Card>
        </BlockStack>
      </Layout.Section>
    </Layout>
    // </Page>
  );
};

export default HomePage;
